import React, { useState, useEffect } from "react";
import "../css/productmanagement.css";
import { getWithTokenAPI, putAPI } from "../../utils/Api";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  CircularProgress,
  Box,
  Button,
  Typography,
  Pagination,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import ProductionQuantityLimitsIcon from "@mui/icons-material/ProductionQuantityLimits";
import { FaBoxes } from "react-icons/fa";

function ProductManagement() {
  const primaryGreen = "#DC3545";
  const lightGreen = "#ffe5e5";

  const [products, setProducts] = useState([]);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const currentProducts = filteredProducts; // Use filteredProducts for pagination
  const [activeTab, setActiveTab] = useState("active");
  const [errorMsg, setErrorMsg] = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const permissions = JSON.stringify(localStorage.getItem("permissions"));

  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [approvalStatus, setApprovalStatus] = useState("");

  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const [productsPerPage] = useState(10);

  const handleSellerClick = (sellerId) => {
    if (sellerId) {
      navigate(`/backoffice/seller-details/${sellerId}`);
    }
  };

  const fetchProducts = async (filterType = null, filterValue = null) => {
    const token = localStorage.getItem("token");
    try {
      let url = "/product/find-all";
      const queryParams = [];

      if (filterType && filterValue) {
        queryParams.push(`${filterType}=${filterValue}`);
      }

      if (queryParams.length > 0) {
        url += `?${queryParams.join("&")}`;
      }

      const res = await getWithTokenAPI(token, url);
      return res?.data?.Product || [];
    } catch (error) {
      console.error("Fetch error:", error);
      setErrorMsg("Failed to load products");
      toast.error("Failed to load products.");
      return [];
    }
  };

  // Edit modal open handler
  const handleEditClick = (product) => {
    setSelectedProduct(product);
    setApprovalStatus(product.Approval_status || "Pending");
    setShowEditModal(true);
  };

  // Update approval status
  const updateApprovalStatus = async () => {
    const token = localStorage.getItem("token");
    setLoading(true);
    try {
      const res = await putAPI(token, `/product/update`, {
        _id: selectedProduct._id,
        Approval_status: approvalStatus,
        product_image: selectedProduct.product_image,

      });

      if (!res.success && res.statusCode !== 200) {
        throw new Error(res.message || "Failed to update approval status");
      }
      toast.success(res.message || "Approval status updated successfully", {
        autoClose: 1500,
        onClose: () => {
          handleTabChange(activeTab);
        },
      });

      setShowEditModal(false);
    } catch (error) {
      console.error("Update error:", error);
      toast.error(error.message || "Failed to update approval status");
    } finally {
      setLoading(false);
    }
  };

  const updateProductStatus = async (productId, currentStatus) => {
    const token = localStorage.getItem("token");
    const newStatus = currentStatus === "Active" ? "Inactive" : "Active";

    setProducts((prev) =>
      prev.map((p) => (p._id === productId ? { ...p, status: newStatus } : p))
    );
    setFilteredProducts((prev) =>
      prev.map((p) => (p._id === productId ? { ...p, status: newStatus } : p))
    );
    setLoading(true);

    try {
      const res = await putAPI(token, `/product/update`, {
        _id: productId,
        status: newStatus,
      });

      if (!res.success && res.statusCode !== 200) {
        throw new Error(res.message || "Failed to update status");
      }

      toast.success(res.message || "Status updated successfully", {
        autoClose: 1000,
        onClose: () => {
          handleTabChange(activeTab);
        },
      });
    } catch (error) {
      console.error("Update error:", error);
      toast.error(error.message || "Failed to update status");

      setProducts((prev) =>
        prev.map((p) =>
          p._id === productId ? { ...p, status: currentStatus } : p
        )
      );
      setFilteredProducts((prev) =>
        prev.map((p) =>
          p._id === productId ? { ...p, status: currentStatus } : p
        )
      );
    } finally {
      setLoading(false);
    }
  };

  const handleTabChange = async (tab) => {
    setActiveTab(tab);
    setLoading(true);
    setCurrentPage(1);
    try {
      let productsData = [];
      switch (tab) {
        case "all":
          productsData = await fetchProducts();
          break;
        case "active":
          productsData = await fetchProducts("status", "Active");
          break;
        case "inactive":
          productsData = await fetchProducts("status", "Inactive");
          break;
        case "Pending":
          productsData = await fetchProducts("Approval_status", "Pending");
          break;
        case "Approval":
          productsData = await fetchProducts("Approval_status", "Approval");
          break;
        case "Reject":
          productsData = await fetchProducts("Approval_status", "Reject");
          break;
        default:
          productsData = await fetchProducts("status", "Active");
      }
      setProducts(productsData);
      setFilteredProducts(productsData);
    } catch (error) {
      console.error("Tab change error:", error);
      toast.error("Failed to load products for this tab.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    handleTabChange("active");
  }, []);

  // Get current products for pagination
  const indexOfLastProduct = currentPage * productsPerPage;
  const indexOfFirstProduct = indexOfLastProduct - productsPerPage;
  const currentproducts = currentProducts.slice(
    indexOfFirstProduct,
    indexOfLastProduct
  );

  const totalPages = Math.ceil(currentProducts.length / productsPerPage);

  const handlePageChange = (event, value) => {
    setCurrentPage(value);
  };

  return (
    <div className="container-fluid" style={{ position: "relative" }}>
      {showEditModal && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(0, 0, 0, 0.5)",
            zIndex: 1040, // below modal (1050) but above everything else
            opacity: showEditModal ? 1 : 0,
            transition: "opacity 0.15s linear",
          }}
        />
      )}

      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          mb: 3,
          p: 2,
          backgroundColor: lightGreen,
          borderRadius: 2,
          borderLeft: `5px solid ${primaryGreen}`,
        }}
      >
        <ProductionQuantityLimitsIcon
          sx={{ color: primaryGreen, fontSize: 40, mr: 2 }}
        />
        <Box>
          <Typography
            variant="h4"
            component="h1"
            sx={{ color: primaryGreen, fontWeight: "bold" }}
          >
            Product
          </Typography>
          <Typography variant="body1" sx={{ color: "text.secondary" }}>
            Manage Products
          </Typography>
        </Box>
      </Box>

      {/* Tab Navigation */}
      <div className="mb-3 row justify-content-between align-items-center">
        <div className="col-auto">
          <div
            style={{
              backgroundColor: lightGreen,
              padding: "4px",
              borderRadius: "8px",
              display: "inline-flex",
              flexWrap: "wrap",
              justifyContent: "flex-start",
            }}
          >
            <ul
              className="nav nav-tabs"
              style={{
                borderBottom: "none",
                display: "flex",
                flexWrap: "wrap",
                justifyContent: "center",
              }}
            >
              <li className="nav-item">
                <button
                  className="nav-link"
                  style={{
                    backgroundColor:
                      activeTab === "active" ? "#DC3545" : "white",
                    color: activeTab === "active" ? "white" : "#DC3545",
                    border: `1px solid ${
                      activeTab === "active" ? "#DC3545" : "white"
                    }`,
                    marginRight: "4px",
                    marginBottom: "2px",
                    fontWeight: "bold",
                    borderRadius: "4px",
                    minWidth: "120px",
                    textTransform: "none",
                  }}
                  onClick={() => handleTabChange("active")}
                >
                  Active
                </button>
              </li>
              <li className="nav-item">
                <button
                  className="nav-link"
                  style={{
                    backgroundColor:
                      activeTab === "inactive" ? "#DC3545" : "white",
                    color: activeTab === "inactive" ? "white" : "#DC3545",
                    border: `1px solid ${
                      activeTab === "inactive" ? "#DC3545" : "white"
                    }`,
                    marginRight: "4px",
                    marginBottom: "2px",
                    fontWeight: "bold",
                    borderRadius: "4px",
                    minWidth: "120px",
                    textTransform: "none",
                  }}
                  onClick={() => handleTabChange("inactive")}
                >
                  Inactive
                </button>
              </li>
              <li className="nav-item">
                <button
                  className="nav-link"
                  style={{
                    backgroundColor:
                      activeTab === "Pending" ? "#DC3545" : "white",
                    color: activeTab === "Pending" ? "white" : "#DC3545",
                    border: `1px solid ${
                      activeTab === "Pending" ? "#DC3545" : "white"
                    }`,
                    marginRight: "4px",
                    marginBottom: "2px",
                    fontWeight: "bold",
                    borderRadius: "4px",
                    minWidth: "120px",
                    textTransform: "none",
                  }}
                  onClick={() => handleTabChange("Pending")}
                >
                 Approval Pending
                </button>
              </li>
              <li className="nav-item">
                <button
                  className="nav-link"
                  style={{
                    backgroundColor:
                      activeTab === "Approval" ? "#DC3545" : "white",
                    color: activeTab === "Approval" ? "white" : "#DC3545",
                    border: `1px solid ${
                      activeTab === "Approval" ? "#DC3545" : "white"
                    }`,
                    marginRight: "4px",
                    marginBottom: "2px",
                    fontWeight: "bold",
                    borderRadius: "4px",
                    minWidth: "120px",
                    textTransform: "none",
                  }}
                  onClick={() => handleTabChange("Approval")}
                >
                  Approved
                </button>
              </li>
              <li className="nav-item">
                <button
                  className="nav-link"
                  style={{
                    backgroundColor:
                      activeTab === "Reject" ? "#DC3545" : "white",
                    color: activeTab === "Reject" ? "white" : "#DC3545",
                    border: `1px solid ${
                      activeTab === "Reject" ? "#DC3545" : "white"
                    }`,
                    marginRight: "0px",
                    marginBottom: "2px",
                    fontWeight: "bold",
                    borderRadius: "4px",
                    minWidth: "120px",
                    textTransform: "none",
                  }}
                  onClick={() => handleTabChange("Reject")}
                >
                  Rejected
                </button>
              </li>
            </ul>
          </div>
        </div>

        {permissions.includes("create_product") && (
          <Button
            variant="contained"
            sx={{
              backgroundColor: primaryGreen,
              "&:hover": {
                backgroundColor: "white",
                border: "2px solid #DC3545",
                color: "#DC3545",
              },
              color: "white",
              fontWeight: "bold",
              textTransform: "none",
              width: { xs: "100%", md: "auto" },
              mt: { xs: 2, md: 0 },
            }}
            onClick={() => navigate("/backoffice/createproduct")}
          >
            <i className="bi bi-plus-circle me-2"></i>Add Product
          </Button>
        )}
      </div>
      <div className="row">
        <div className="col-12">
          <ToastContainer position="top-center" autoClose={1500} />

          <div className="table-responsive">
            {loading ? (
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  height: "200px",
                }}
              >
                <CircularProgress sx={{ color: primaryGreen }}/>
                <Typography sx={{ ml: 2, color: primaryGreen }}>Loading products...</Typography>
              </Box>
            ) : (
              <>
                <table className="table m-0">
                  {" "}
                  <thead className="sticky-header">
                    <tr>
                      <th
                        className="table-header"
                        style={{ color: "#777", textAlign: "left" }}
                      >
                        S.No
                      </th>
                      <th
                        className="table-header"
                        style={{ color: "#777", textAlign: "left" }}
                      >
                        Image
                      </th>
                      <th
                        className="table-header"
                        style={{ color: "#777", textAlign: "left" }}
                      >
                        Name
                      </th>
                      <th
                        className="table-header"
                        style={{ color: "#777", textAlign: "left" }}
                      >
                        Variant
                      </th>
                      <th
                        className="table-header"
                        style={{ color: "#777", textAlign: "left" }}
                      >
                        Seller Name
                      </th>
                      <th
                        className="table-header"
                        style={{ color: "#777", textAlign: "left" }}
                      >
                        Original Price(Ksh)
                      </th>
                      <th
                        className="table-header"
                        style={{ color: "#777", textAlign: "left" }}
                      >
                        Discount Price(Ksh)
                      </th>
                      <th
                        className="table-header"
                        style={{ color: "#777", textAlign: "left" }}
                      >
                        Updated By
                      </th>
                      <th
                        className="table-header"
                        style={{ color: "#777", textAlign: "left" }}
                      >
                        Status
                      </th>
                      <th
                        className="table-header"
                        style={{ color: "#777", textAlign: "left" }}
                      >
                        Approval Status
                      </th>
                      {permissions.includes("update_product") && (
                        <th
                          className="table-header"
                          style={{ color: "#777", textAlign: "left" }}
                        >
                          Action
                        </th>
                      )}
                    </tr>
                  </thead>
                  <tbody>
                    {currentProducts.length > 0 ? (
                      currentProducts.map((product, index) => (
                        <tr
                          key={product._id}
                          style={{ verticalAlign: "middle" }}
                        >
                          <td style={{ textAlign: "left" }}>
                            {indexOfFirstProduct + index + 1}
                          </td>
                          <td style={{ textAlign: "left" }}>
                            <img
                              src={product.product_image?.[0]}
                              alt={product.name}
                              style={{
                                width: "50px",
                                height: "50px",
                                objectFit: "cover",
                                borderRadius: "5px",
                              }}
                            />
                          </td>
                          <td
                            onClick={() => handleEditClick(product)}
                            style={{
                              fontWeight: "bold",
                              color: primaryGreen,
                              cursor: "pointer",
                              "&:hover": { textDecoration: "underline" },
                              fontSize: { xs: "0.8rem", md: "1rem" },
                              textAlign:
                                "left" /* Added for explicit left alignment */,
                            }}
                          >
                            {product.name}
                          </td>
                          <td style={{ textAlign: "left" }}>
                            {product.variant}
                          </td>
                          <td style={{ textAlign: "left" }}>
                            {product.seller_id?.name ? (
                              <span
                                onClick={() =>
                                  handleSellerClick(product.seller_id?._id)
                                }
                                style={{
                                  cursor: "pointer",
                                  color: "#DC3545",
                                  textDecoration: "none",
                                  "&:hover": { textDecoration: "underline" },
                                }}
                                title={`View details for ${product.seller_id.name}`}
                              >
                                {product.seller_id.name}
                              </span>
                            ) : (
                              "N/A"
                            )}
                          </td>
                          <td style={{ textAlign: "left" }}>
                            {product.original_price}
                          </td>
                          <td style={{ textAlign: "left" }}>
                            {product.discount_price}
                          </td>
                          <td style={{ textAlign: "left" }}>
                            {product.updated_by?.name || "-"}
                          </td>
                          <td style={{ textAlign: "left" }}>
                            <div className="form-check form-switch d-flex justify-content-start">
                              {" "}
                              {/* Changed to justify-content-start */}
                              <input
                                className="form-check-input"
                                type="checkbox"
                                role="switch"
                                checked={product.status === "Active"}
                                onChange={() =>
                                  updateProductStatus(
                                    product._id,
                                    product.status
                                  )
                                }
                                style={{
                                  width: "3em",
                                  height: "1.5em",
                                  backgroundColor:
                                    product.status === "Active"
                                      ? "#DC3545"
                                      : "transparent",
                                  border:
                                    product.status !== "Active"
                                      ? "2px solid #DC3545"
                                      : "none",
                                  boxShadow: "none",
                                }}
                              />
                            </div>
                          </td>
                          <td style={{ textAlign: "left" }}>
                            <span
                              className={`badge ${
                                product.Approval_status === "Pending"
                                  ? "bg-warning"
                                  : product.Approval_status === "Approval"
                                  ? "bg-success"
                                  : product.Approval_status === "Reject"
                                  ? "bg-danger"
                                  : "bg-secondary"
                              }`}
                            >
                              {product.Approval_status || "Pending"}
                            </span>
                          </td>
                          {permissions.includes("update_product") && (
                            <td style={{ textAlign: "left" }}>
                              <i
                                className="bi bi-pencil-square text-success fs-5 cursor-pointer"
                                title="Edit"
                                onClick={() => handleEditClick(product)}
                              ></i>
                            </td>
                          )}
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan="10" className="text-center">
                          {" "}
                          {/* Keeping this centered for the "No products found" message */}
                          No products found
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
                {/* Pagination Controls */}
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "end",
                    mt: 3,
                    pb: 2,
                  }}
                >
                  <Pagination
                    count={totalPages}
                    page={currentPage}
                    onChange={handlePageChange}
                    color="green"
                    sx={{
                      "& .MuiPaginationItem-root": {
                        color: primaryGreen,
                      },
                      "& .MuiPaginationItem-root.Mui-selected": {
                        backgroundColor: primaryGreen,
                        color: "white",
                      },
                    }}
                  />
                </Box>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Edit Modal for Approval Status */}
      {/* {showEditModal && selectedProduct && (
        <div
          className="modal show d-block"
          tabIndex="-1"
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 1050,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            overflow: "auto",
          }}
        >
          <div className="modal-dialog modal-lg">
            <div className="modal-content">
              <div
                className="modal-header text-white"
                style={{
                  backgroundColor: "#DC3545",
                  color: "white",
                  fontSize: "bold",
                }}
              >
                <h5 className="modal-title">Update Approval Status</h5>
                <button
                  type="button"
                  className="btn-close btn-close-white"
                  onClick={() => setShowEditModal(false)}
                ></button>
              </div>
              <div className="modal-body">
                <div className="row mb-3">
                  <div className="col-md-4">
                    <img
                      src={selectedProduct.product_image?.[0]}
                      alt={selectedProduct.name}
                      className="img-fluid rounded"
                      style={{ maxHeight: "200px" }}
                    />
                  </div>
                  <div className="col-md-8">
                    <div className="row">
                      {[
                        { label: "Name", value: selectedProduct.name },
                        { label: "Variant", value: selectedProduct.variant },
                        {
                          label: "Original Price",
                          value: selectedProduct.original_price,
                        },
                        {
                          label: "Discount Price",
                          value: selectedProduct.discount_price,
                        },
                        { label: "Stock", value: selectedProduct.stock },
                        {
                          label: "Updated By",
                          value: selectedProduct.updated_by?.name || "-",
                        },
                        {
                          label: "long_description",
                          value: selectedProduct.long_description,
                        },
                        {
                          label: "short_description",
                          value: selectedProduct.short_description,
                        },
                      ].map((field, i) => (
                        <div className="col-md-6 mb-2" key={i}>
                          <label className="form-label text-muted small">
                            {field.label}
                          </label>
                          <input
                            type="text"
                            className="form-control"
                            value={field.value || "-"}
                            readOnly
                          />
                        </div>
                      ))}
                    </div>
                  </div>
                </div>

                <div className="mb-3">
                  <label className="form-label">Approval Status</label>
                  <select
                    className="form-select"
                    value={approvalStatus}
                    onChange={(e) => setApprovalStatus(e.target.value)}
                  >
                    <option value="Pending">Pending</option>
                    <option value="Approval">Approved</option>
                    <option value="Reject">Rejected</option>
                  </select>
                </div>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setShowEditModal(false)}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn"
                  onClick={updateApprovalStatus}
                  style={{
                    backgroundColor: "#DC3545",
                    color: "white",
                    fontSize: "bold",
                  }}
                >
                  Update Status
                </button>
              </div>
            </div>
          </div>
        </div>
      )} */}
{showEditModal && selectedProduct && (
  <div
    className="modal show d-block"
    tabIndex="-1"
    style={{
      position: "fixed",
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      zIndex: 1050,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      overflow: "auto",
    }}
  >
    <div className="modal-dialog modal-xl">
      <div className="modal-content">
        <div
          className="modal-header text-white"
          style={{
            backgroundColor: "#DC3545",
            color: "white",
            fontWeight: "bold",
          }}
        >
          <h5 className="modal-title">Update Product</h5>
          <button
            type="button"
            className="btn-close btn-close-white"
            onClick={() => setShowEditModal(false)}
          ></button>
        </div>
        <div className="modal-body">
          <div className="row mb-3">
            <div className="col-md-4 d-flex flex-column">
              <div className="mb-3">
                <img
                  src={selectedProduct.product_image?.[0] || "https://via.placeholder.com/200"}
                  alt={selectedProduct.name}
                  className="img-fluid rounded mb-2"
                  style={{ 
                    maxHeight: "200px",
                    border: "2px solid #DC3545",
                    objectFit: "cover",
                  }}
                />
                <input
                  type="text"
                  className="form-control"
                  value={selectedProduct.product_image?.[0] || ""}
                  onChange={(e) =>
                    setSelectedProduct(prev => ({
                      ...prev,
                      product_image: [e.target.value]
                    }))
                  }
                  placeholder="Enter image URL"
                />
              </div>
            </div>
            <div className="col-md-8">
              <div className="row">
                {[
                  { label: "Name", value: selectedProduct.name },
                  { label: "Variant", value: selectedProduct.variant },
                  { label: "Original Price", value: selectedProduct.original_price },
                  { label: "Discount Price", value: selectedProduct.discount_price },
                  { label: "Status", value: selectedProduct.status },
                  { label: "Current Approval Status", value: selectedProduct.Approval_status || "Pending" },
                ].map((field, i) => (
                  <div className="col-md-6 mb-2" key={i}>
                    <label className="form-label text-muted small">
                      {field.label}
                    </label>
                    <input
                      type="text"
                      className="form-control"
                      value={field.value || "-"}
                      readOnly
                    />
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="mb-3">
            <label className="form-label">Update Approval Status</label>
            <select
              className="form-select"
              value={approvalStatus}
              onChange={(e) => setApprovalStatus(e.target.value)}
            >
              <option value="Pending">Pending</option>
              <option value="Approval">Approved</option>
              <option value="Reject">Rejected</option>
            </select>
          </div>
        </div>
        <div className="modal-footer">
          <button
            type="button"
            className="btn btn-secondary"
            onClick={() => setShowEditModal(false)}
          >
            Cancel
          </button>
          <button
            type="button"
            className="btn text-white"
            style={{
              backgroundColor: "#DC3545",
              fontWeight: "bold"
            }}
            onClick={updateApprovalStatus}
            disabled={loading}
          >
            {loading ? "Updating..." : "Update"}
          </button>
        </div>
      </div>
    </div>
  </div>
)}

    </div>
  );
}

export default ProductManagement;
